<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { Pie } from 'vue-chartjs';
    import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
    import { applyChartDefaults, generateColors } from '@shared/modules/chartjs-defaults';
    import type { UnsubscribeReasonStats } from '../../interfaces';
    import List from '@shared/components/List/List.vue';
    import BSToggleButtons from '@shared/components/BSToggleButtons.vue';
    import { ListViewSVG, StatsSVG } from '@cwd/shared/assets/vector/ui/IconsUI';

    ChartJS.register(ArcElement, Tooltip, Legend);
    applyChartDefaults();

    const props = defineProps<{
        unsubscribeReasonStats: UnsubscribeReasonStats[];
    }>();

    const view = ref<'table' | 'chart'>('table');

    const viewOptions = [
        { name: $Resources.DisplayOptionList, value: 'table', icon: ListViewSVG },
        { name: $Resources.DisplayOptionChart, value: 'chart', icon: StatsSVG },
    ];

    const reasonLabels: Record<number, string> = {
        1: $Resources.UnsubscribeReasonNoLongerWant,
        2: $Resources.UnsubscribeReasonTooFrequent,
        3: $Resources.UnsubscribeReasonNeverSignedUp,
        4: $Resources.UnsubscribeReasonInappropriate,
        5: $Resources.UnsubscribeReasonSpam,
        6: $Resources.UnsubscribeReasonOther,
    };

    const hasReasons = computed(() => props.unsubscribeReasonStats.length > 0);
    const total = computed(() => props.unsubscribeReasonStats.reduce((sum, r) => sum + r.Count, 0));

    const pieColors = computed(() => generateColors(props.unsubscribeReasonStats.length));

    const chartData = computed(() => ({
        labels: props.unsubscribeReasonStats.map((r) => reasonLabels[r.ReasonValue] ?? $Resources.UnsubscribeReasonUnknown),
        datasets: [
            {
                data: props.unsubscribeReasonStats.map((r) => r.Count),
                backgroundColor: pieColors.value,
            },
        ],
    }));

    const chartOptions = computed(() => ({
        responsive: true,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const count = ctx.parsed;
                        const pct = total.value > 0 ? Math.round((count / total.value) * 100) : 0;
                        return ` ${count} (${pct}%)`;
                    },
                },
            },
        },
    }));

    const updateView = (newView: string) => {
        view.value = newView as 'table' | 'chart';
    };
</script>

<template>
    <div v-if="hasReasons" class="print-block">
        <div class="d-flex align-items-baseline justify-content-between gap-sm m-sm-b">
            <h2 class="h3 m-0-t m-0-b">{{ $Resources.IssueStatisticsUnsubscribeReasonsHeading }}</h2>

            <BSToggleButtons :value="view" :options="viewOptions" hide-labels button-class="btn-sm" @update:value="updateView" />
        </div>

        <List
            v-if="view === 'table'"
            class="th2-text-right th3-text-right"
            display-as="table"
            itemId="ReasonValue"
            :items="unsubscribeReasonStats"
            :table-headers="[
                $Resources.IssueStatisticsUnsubscribeReasonLabel,
                $Resources.IssueStatisticsUnsubscribeCountLabel,
                $Resources.IssueStatisticsUnsubscribeShareLabel,
            ]"
            :all-loaded="true"
            hide-header
            hide-toolbar>
            <template #item="{ item }">
                <td>{{ reasonLabels[item.ReasonValue] ?? $Resources.UnsubscribeReasonUnknown }}</td>
                <td class="text-right strong">{{ item.Count }}</td>
                <td class="text-right">{{ total > 0 ? Math.round((item.Count / total) * 100) + '%' : '-' }}</td>
            </template>
        </List>

        <div v-else class="chart-container chart-container-panel">
            <div class="d-flex align-items-center justify-content-center gap-lg">
                <div class="h-400">
                    <Pie :data="chartData" :options="chartOptions" />
                </div>

                <ul class="list-unstyled m-0">
                    <li v-for="(stat, i) in unsubscribeReasonStats" :key="stat.ReasonValue" class="d-flex align-items-center gap-sm m-xs-b">
                        <span :style="{ background: pieColors[i] }" style="width: 12px; height: 12px; border-radius: 2px; flex-shrink: 0"></span>
                        <span>
                            {{ reasonLabels[stat.ReasonValue] ?? $Resources.UnsubscribeReasonUnknown }}
                            {{ total > 0 ? Math.round((stat.Count / total) * 100) : 0 }}%
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
